/**
 * Ortsprognose von MeteoSchweiz.
 *
 * Die Daten liegen fertig aufbereitet auf unserem Server: für jeden der rund
 * 5500 Prognosepunkte eine kleine JSON unter `/prognose/<id>.json`, dazu ein
 * Verzeichnis `punkte.json`. Entstanden sind sie aus
 * `ch.meteoschweiz.ogd-local-forecasting` (deploy/prognose-update.py) - das ist
 * dieselbe Prognose, die in der MeteoSchweiz-App zu jeder Postleitzahl steht.
 *
 * Warum vorbereitet und nicht hier geholt: die Rohdaten sind je Parameter eine
 * CSV von 33 MB im Langformat, unkomprimiert. Das Telefon lädt stattdessen
 * 250 KB Verzeichnis und ein paar Kilobyte je Ort.
 */
import { mitBasis } from './basis.js';
const BASE = mitBasis('/prognose');

/** Was MeteoSchweiz unter den drei Punktarten versteht. */
export const ARTEN = { 1: 'Station', 2: 'Ort', 3: 'Punkt' };

let verzeichnis = null;
let laedt = null;

/** Das Verzeichnis - einmal geholt, dann im Speicher. */
export async function punkte() {
  if (verzeichnis) return verzeichnis;
  if (!laedt) {
    laedt = (async () => {
      const r = await fetch(`${BASE}/punkte.json`, { cache: 'no-store' });
      if (!r.ok) throw new Error(`Verzeichnis: HTTP ${r.status}`);
      const d = await r.json();
      verzeichnis = {
        lauf: d.lauf,
        basis: d.basis,
        liste: (d.punkte || []).map(([id, name, lat, lng, hoehe, typ, plz]) =>
          ({ id, name, lat, lng, hoehe, typ, plz: plz || '' }))
      };
      return verzeichnis;
    })().finally(() => { laedt = null; });
  }
  return laedt;
}

/**
 * Abstand in Kilometern, flach gerechnet.
 *
 * Für ein paar Dutzend Kilometer in der Schweiz reicht das - der Fehler gegen
 * die Kugel liegt weit unter dem Abstand zwischen zwei Prognosepunkten.
 */
function abstand(aLat, aLng, bLat, bLng) {
  const dy = (aLat - bLat) * 111.2;
  const dx = (aLng - bLng) * 111.2 * Math.cos((aLat * Math.PI) / 180);
  return Math.sqrt(dx * dx + dy * dy);
}

/** Der nächstgelegene Prognosepunkt zu einer Stelle auf der Karte. */
export async function naechster(lat, lng) {
  const { liste } = await punkte();
  let best = null;
  let beste = Infinity;
  for (const p of liste) {
    const d = abstand(lat, lng, p.lat, p.lng);
    if (d < beste) { beste = d; best = p; }
  }
  return best ? { ...best, km: beste } : null;
}

/**
 * Orte nach Namen suchen.
 *
 * Treffer am Wortanfang stehen vorn - wer "Zug" tippt, meint Zug und nicht
 * Zugerberg oder Hinterzug. Bei gleichem Rang entscheidet die Nähe zur Mitte
 * der Karte, sonst steht bei "Buchs" vier Mal dasselbe Wort ohne Ordnung.
 */
export async function suche(text, { lat, lng, max = 8 } = {}) {
  const q = (text || '').trim().toLowerCase();
  if (q.length < 2) return [];
  const { liste } = await punkte();
  const treffer = [];
  const nurZiffern = /^\d+$/.test(q);
  for (const p of liste) {
    const name = p.name.toLowerCase();
    const i = name.indexOf(q);
    // Vier Ziffern sind eine Postleitzahl, kein Ortsname.
    const plz = nurZiffern && p.plz.startsWith(q);
    if (i < 0 && !plz) continue;
    const rang = plz ? (p.plz === q ? 0 : 1)
      : (name === q ? 0 : (i === 0 ? 1 : (/\s|-/.test(name[i - 1]) ? 2 : 3)));
    const km = Number.isFinite(lat) ? abstand(lat, lng, p.lat, p.lng) : 0;
    treffer.push({ ...p, rang, km });
  }
  treffer.sort((a, b) => a.rang - b.rang || a.km - b.km || a.name.localeCompare(b.name, 'de-CH'));
  return treffer.slice(0, max);
}

/**
 * Die Prognose eines Punkts.
 *
 * `stunden[].h` zählt ab `basis`, `stunden[].d` zeigt auf den Tag in `tage` -
 * welcher Kalendertag das ist, hat schon der Server entschieden, damit ein
 * Browser in einer anderen Zeitzone nicht anders gruppiert als die Tagesliste.
 */
export async function prognose(id) {
  const r = await fetch(`${BASE}/${encodeURIComponent(id)}.json`, { cache: 'no-store' });
  if (!r.ok) throw new Error(`Prognose: HTTP ${r.status}`);
  const d = await r.json();
  const basis = new Date(d.basis);
  return {
    ...d,
    basis,
    stunden: (d.stunden || []).map((s) => ({
      ...s,
      zeit: new Date(basis.getTime() + s.h * 3600000)
    }))
  };
}
